export {
  UTM_STORAGE_KEY,
  captureUtmParams,
  getUtmParams,
  formatUtmAdminBlock,
  appendUtmToAdminText,
} from './utm';

export function getReadinessFromBands(score, bands) {
  const sorted = [...bands].sort((a, b) => b.min - a.min);
  return sorted.find((band) => score >= band.min) || sorted[sorted.length - 1];
}

export function isScoredBlock(block) {
  return block.scoring !== false;
}

export function isScoredQuestion(question, block) {
  if (block?.scoring === false) return false;
  if (question.type === 'multi_choice') return false;
  return true;
}

export function formatAnswerValue(value, scale, question = null) {
  if (value === undefined || value === null) return 'Не отвечено';
  if (question?.type === 'multi_choice' && question.options) {
    const selected = Array.isArray(value) ? value : [];
    if (selected.length === 0) return 'Не отмечено';
    return selected
      .map((v) => question.options.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  }
  if (scale.type === 'likert' && scale.labels) {
    return scale.labels[value] ?? String(value);
  }
  return scale.answerSuffix ? `${value}${scale.answerSuffix}` : String(value);
}

export function computeChecklistScore(checklistData, answers) {
  let score = 0;
  checklistData.blocks.forEach((block) => {
    if (!isScoredBlock(block)) return;
    block.questions.forEach((q) => {
      if (!isScoredQuestion(q, block)) return;
      score += Number(answers[q.id]) || 0;
    });
  });
  return score;
}

export function countAnsweredQuestions(checklistData, answers) {
  let count = 0;
  checklistData.blocks.forEach((block) => {
    block.questions.forEach((q) => {
      const a = answers[q.id];
      if (q.type === 'multi_choice') {
        if (Array.isArray(a) && a.length > 0) count += 1;
      } else if (a !== undefined) {
        count += 1;
      }
    });
  });
  return count;
}

/**
 * ICP: роль, размер, отрасль, триггер - 1 балл за совпадение (макс. 4).
 * profile: operations - пилот COO (strategy); sales - чек-лист отдела продаж.
 */
function labelForValue(options, value) {
  if (!value || !options) return value || '-';
  return options.find((o) => o.value === value)?.label ?? value;
}

/** Текст ICP только для письма администратору (не показывать пользователю) */
export function formatIcpAdminBlock(lead, profile, icpFields) {
  if (!icpFields) return '';
  const score = computeIcpScore(lead, profile);
  return [
    '--- ICP (только для администратора) ---',
    `ICP-скоринг: ${score} из 4`,
    `Роль: ${labelForValue(icpFields.roles, lead.role)}`,
    `Размер компании: ${labelForValue(icpFields.sizes, lead.companySize)}`,
    `Отрасль: ${labelForValue(icpFields.industries, lead.industry)}`,
    `Триггер: ${labelForValue(icpFields.triggers, lead.trigger)}`,
    '---',
  ].join('\n');
}

export function computeIcpScore(lead, profile = 'operations') {
  let score = 0;

  if (profile === 'sales') {
    if (lead.role === 'commercial_director' || lead.role === 'rop' || lead.role === 'rom') score += 1;
    if (lead.companySize === '100-500' || lead.companySize === '500plus') score += 1;
    if (lead.industry === 'trade' || lead.industry === 'it') score += 1;
    if (['sales_load', 'manual_content', 'efficiency', 'crm'].includes(lead.trigger)) score += 1;
    return score;
  }

  if (lead.role === 'coo') score += 1;
  if (lead.companySize === '100-500' || lead.companySize === '500plus') score += 1;
  if (lead.industry === 'trade' || lead.industry === 'it') score += 1;
  if (['ops_load', 'efficiency', 'compliance'].includes(lead.trigger)) score += 1;
  return score;
}

export function getPillarScores(checklistData, answers, maxPerQuestion = 2) {
  const byPillar = {};
  checklistData.blocks.forEach((block) => {
    if (!isScoredBlock(block)) return;
    const pillar = block.pillar || block.title;
    if (!byPillar[pillar]) byPillar[pillar] = { score: 0, max: 0 };
    block.questions.forEach((q) => {
      if (!isScoredQuestion(q, block)) return;
      const val = answers[q.id];
      const num = Number(val);
      if (!Number.isNaN(num)) {
        byPillar[pillar].score += num;
      }
      byPillar[pillar].max += maxPerQuestion;
    });
  });
  return byPillar;
}

export function getPillarStatus(score, max) {
  const ratio = max > 0 ? score / max : 0;
  if (ratio >= 0.75) return { label: 'Ок', className: 'text-green-600 bg-green-50 border-green-200' };
  if (ratio >= 0.45) return { label: 'Риск', className: 'text-yellow-700 bg-yellow-50 border-yellow-200' };
  return { label: 'Блокер', className: 'text-red-600 bg-red-50 border-red-200' };
}

export function getOperationsRecommendations(pillarScores, pillarLabels) {
  const tips = [];
  Object.entries(pillarScores).forEach(([pillar, { score, max }]) => {
    const ratio = max > 0 ? score / max : 0;
    const name = pillarLabels[pillar] || pillar;
    if (ratio < 0.45) {
      if (pillar === 'data') {
        tips.push(`Зафиксируйте перечень данных пилота и правила выгрузки - без этого внешние API недопустимы. (${name})`);
      } else if (pillar === 'owner') {
        tips.push(`Назначьте владельца эффекта из операций и одну метрику пилота - до выбора вендора. (${name})`);
      } else if (pillar === 'integrations') {
        tips.push(`Опишите первый контур на проде и поддержку после POC - иначе демо не станет внедрением. (${name})`);
      } else if (pillar === 'team') {
        tips.push(`Синхронизируйте операции, ИТ и вендора одним координатором и коротким обучением. (${name})`);
      } else if (pillar === 'budget') {
        tips.push(`Заложите бюджет на интеграции и дату ревью пилота - не только лицензию на модель. (${name})`);
      } else {
        tips.push(`Усильте блок «${name}» - сейчас это слабое место для старта пилота.`);
      }
    }
  });
  return tips.slice(0, 5);
}

const CHECKLIST_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidChecklistEmail(value) {
  return CHECKLIST_EMAIL_REGEX.test(String(value || '').trim());
}
